// Service Worker utility functions to handle caching issues

export async function clearAllCaches(): Promise<void> {
  if ('caches' in window) {
    const cacheNames = await caches.keys()
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName)),
    )
  }
}

export async function forceServiceWorkerUpdate(): Promise<void> {
  if ('serviceWorker' in navigator) {
    // Clear all caches first
    await clearAllCaches()

    // Get the current registration
    const registration = await navigator.serviceWorker.getRegistration()

    if (registration) {
      // Force update the service worker
      await registration.update()

      // If there's a waiting worker, activate it
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
    }
  }
}

export async function unregisterServiceWorker(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      const result = await registration.unregister()
      // Clear caches after unregistering
      await clearAllCaches()
      return result
    }
  }
  return false
}

// Function to check if we're getting HTML instead of JS (the main issue)
export function isStaleCache(): boolean {
  // Check if any script tags failed to load properly
  const scripts = document.querySelectorAll('script[src]')
  return Array.from(scripts).some((script) => {
    const scriptElement = script as HTMLScriptElement
    return scriptElement.onerror !== null
  })
}

// Emergency function to clear everything and reload
export async function emergencyReset(): Promise<void> {
  try {
    // Unregister service worker
    await unregisterServiceWorker()

    // Clear all storage
    if ('localStorage' in window) {
      localStorage.clear()
    }
    if ('sessionStorage' in window) {
      sessionStorage.clear()
    }

    // Force reload
    window.location.reload()
  }
  catch (error) {
    console.error('Emergency reset failed:', error)
    // Last resort - just reload
    window.location.reload()
  }
}
